import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { get, post } from 'api/fetch';
import {
  NI_GIFT_CARD_CAMPAIGN_ID,
  NI_REACTIVATION_GIFT_CARD_CAMPAIGN_ID,
  NI_REFERRAL_CAMPAIGN_ID,
  REFERRAL_TOKEN_KEY
} from 'products/nomad_insurance/constants';

export const fetchReferralLink = createAsyncThunk(
  'nomad_insurance/referrals/fetchReferralLink',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { userID } = state.app.user;
    if (!userID) {
      return;
    }

    try {
      const referralLink = await get(`/referrals/tokens/v1/users/${userID}`, {
        isNIOrchestrator: true
      });

      return referralLink;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchReferralLinks = createAsyncThunk(
  'nomad_insurance/referrals/fetchReferralLinks',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { userID } = state.app.user;
    if (!userID) {
      return;
    }

    try {
      const referralLinks = await get(`/referrals/tokens/v2/users/${userID}`, {
        isNIOrchestrator: true
      });

      return referralLinks;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchReferredBy = createAsyncThunk(
  'nomad_insurance/referrals/fetchReferredBy',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { userID } = state.app.user;

    try {
      const response = await get(
        `/referrals/v1/users/${userID}/campaigns/${NI_REFERRAL_CAMPAIGN_ID}`,
        {
          isReferral: true
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchCreditsSummary = createAsyncThunk(
  'nomad_insurance/referrals/fetchCreditsSummary',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { userID } = state.app.user;

    try {
      const creditsSummary = await get(
        `/referrals/credits/v1/users/${userID}/campaigns/${NI_REFERRAL_CAMPAIGN_ID}/summary`,
        {
          isNIOrchestrator: true
        }
      );

      return creditsSummary;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchReferralCredits = createAsyncThunk(
  'nomad_insurance/referrals/fetchReferralCredits',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { userID } = state.app.user;

    try {
      const response = await get(
        `/credits/v1/users/${userID}/campaigns/${NI_REFERRAL_CAMPAIGN_ID},${NI_REACTIVATION_GIFT_CARD_CAMPAIGN_ID},${NI_GIFT_CARD_CAMPAIGN_ID}`,
        {
          isReferral: true
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const registerLinkClickedV2 = createAsyncThunk(
  'referrals/v2/registerLinkClicked',
  async (referralToken, { getState, rejectWithValue }) => {
    const state = getState();
    const { userID } = state.app.user;

    if (!userID) {
      return;
    }

    try {
      return await post(`/referrals/v2/${referralToken}`, undefined, {
        isNIOrchestrator: true
      });
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      localStorage.removeItem(REFERRAL_TOKEN_KEY);
    }
  }
);

// ================== Reducers ================== //

const slice = createSlice({
  name: 'nomad_insurance/referrals',
  initialState: {
    creditsSummary: null,
    creditsSummaryLoading: false,
    creditsSummaryError: false,
    referralLink: null,
    daysRemainingForEligibility: null,
    referralLinkLoading: false,
    referralLinkError: false,
    referredBy: null,
    referredByLoading: false,
    referredByError: null,
    credits: null,
    creditsLoading: false,
    creditsError: null,
    tokenApplicationCredit: null,
    tokenApplicationLoading: false,
    tokenApplicationError: null,
    referralLinks: null
  },
  reducers: {
    resetApplyToken: (state) => {
      state.tokenApplicationCredit = null;
      state.tokenApplicationLoading = false;
      state.tokenApplicationError = null;
    }
  },
  extraReducers: {
    [fetchReferralLink.pending]: (state) => {
      state.referralLinkLoading = true;
    },
    [fetchReferralLink.rejected]: (state, { payload }) => {
      state.referralLink = null;
      state.referralLinkLoading = false;
      state.referralLinkError = payload;
    },
    [fetchReferralLink.fulfilled]: (state, { payload }) => {
      state.referralLinkLoading = false;
      state.referralLinkError = null;
      state.referralLink = payload?.referralLink;
      state.daysRemainingForEligibility = payload?.daysRemainingForEligibility;
    },
    [fetchReferralLinks.pending]: (state) => {
      state.referralLinkLoading = true;
    },
    [fetchReferralLinks.rejected]: (state, { payload }) => {
      state.referralLinks = null;
      state.referralLinkLoading = false;
      state.referralLinkError = payload;
    },
    [fetchReferralLinks.fulfilled]: (state, { payload }) => {
      state.referralLinkLoading = false;
      state.referralLinkError = null;
      state.referralLinks = payload;
    },
    [fetchCreditsSummary.pending]: (state) => {
      state.creditsSummaryLoading = true;
    },
    [fetchCreditsSummary.rejected]: (state, { payload }) => {
      state.creditsSummary = null;
      state.creditsSummaryLoading = false;
      state.creditsSummaryError = payload;
    },
    [fetchCreditsSummary.fulfilled]: (state, { payload }) => {
      state.creditsSummaryLoading = false;
      state.creditsSummaryError = null;
      state.creditsSummary = payload;
    },
    [fetchReferredBy.pending]: (state) => {
      state.referredByLoading = true;
      state.referredByError = null;
    },
    [fetchReferredBy.fulfilled]: (state, { payload }) => {
      state.referredByLoading = false;
      state.referredByError = null;
      state.referredBy = payload;
    },
    [fetchReferredBy.rejected]: (state, { payload }) => {
      state.referredByLoading = false;
      state.referredByError = payload;
    },
    [fetchReferralCredits.pending]: (state) => {
      state.creditsLoading = true;
      state.creditsError = null;
    },
    [fetchReferralCredits.fulfilled]: (state, { payload }) => {
      state.creditsLoading = false;
      state.creditsError = null;
      state.credits = payload;
    },
    [fetchReferralCredits.rejected]: (state, { payload }) => {
      state.creditsLoading = false;
      state.creditsError = payload;
    }
  }
});

// ================== Actions ================== //
export const { resetApplyToken, ...actions } = slice.actions;
export default slice.reducer;
